import { Edge, Node } from "@xyflow/react";
import { CollectorNode } from "./getTreeLayout";
import { HierarchyPointNode } from "d3-hierarchy";

type FlowOutput = {
  nodes: Node[];
  edges: Edge[];
};

export function buildTreeLayout(
  root: HierarchyPointNode<CollectorNode>,
): FlowOutput {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const traverse = (
    node: HierarchyPointNode<CollectorNode>,
    parentId?: string,
    isOnlyChildWithDevices = false,
  ) => {
    const nodeId = node.data.name;
    const y = node.y;
    let x = node.x;

    const childY = node.children?.[0]?.y || y + 150; // Used to set the devices linked to this node on the same level as the children nodes. If no children, we just set it to y + 150

    const hasOnlyOneChild = node.children?.length === 1;
    const hasDevices = node.data.devices.length > 0;

    // If the parent has only one child and devices, the child and devices are overlapped so we need to move the child a bit
    if (isOnlyChildWithDevices) {
      const parentX = node.parent?.parent?.x ?? 0;
      x = (node.x - parentX) * 2 + parentX;
    }

    nodes.push({
      id: node.data.name,
      data: node.data.asset,
      position: { x, y },
      type: node.data.type,
    });

    if (parentId) {
      edges.push({
        id: `${parentId}->${nodeId}`,
        source: parentId,
        target: nodeId,
        type: "step",
      });
    }

    node.data.devices.forEach((device, index) => {
      nodes.push({
        id: device.name,
        data: device.asset,
        position: { x: x, y: childY + index * 150 },
        type: "device",
      });
      edges.push({
        id: `${nodeId}->${device.name}`,
        source: nodeId,
        target: device.name,
        type: "step",
      });
    });

    if (node.children?.length) {
      const collectors = node.children.filter(
        (child) => child.data.type == "collector",
      );

      collectors.forEach((child) => {
        traverse(child, nodeId, hasOnlyOneChild && hasDevices);
      });
    }
  };

  traverse(root);

  return { nodes, edges };
}
